import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/components/LoginModal.css';
import { translations } from '../translations';
import { login } from '../contexts/db.User';
import { checkSubscriptionAccess } from '../contexts/db.Subscription';

const LoginModal = ({ onClose, language }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const t = translations[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleLogin();
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await login(email, password);

      if(response && (response.id || response.token)) {
        // Store authentication data
        if (response.token) {
          localStorage.setItem('authToken', response.token);
        }
        
        const userId = response.user?.id || response.id || 'unknown';
        
        if (response.user) {
          localStorage.setItem('userData', JSON.stringify(response.user));
        } else {
          const userData = {
            id: userId,
            email: email,
            first_name: response.first_name || 'User'
          };
          localStorage.setItem('userData', JSON.stringify(userData));
        }
        
        // Close modal
        onClose();
        
        // Check subscription status
        if (userId !== 'unknown') {
          try {
            const subscriptionCheck = await checkSubscriptionAccess(userId);
            
            if (subscriptionCheck.hasAccess) {
              navigate('/');
              window.location.reload();
            } else {
              navigate('/subscription', { 
                state: { 
                  userId: userId,
                  subscriptionStatus: subscriptionCheck.status,
                  fromLogin: true
                } 
              });
            }
          } catch (subscriptionError) {
            console.error('Subscription check error:', subscriptionError);
            navigate('/');
            window.location.reload();
          }
        } else {
          navigate('/');
          window.location.reload();
        }
      } else {
        setError(t.loginFailed || 'Login failed. Please check your credentials');
      }
      
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || err.message || t.loginFailed || 'Login failed');
    } finally {
      setLoading(false);
    }
  };    

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="modal-header">
          <h2 className="modal-title">{t.login}</h2>
          <p className="modal-subtitle">{t.welcomeBack}</p>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="email">{t.email}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.enterEmail}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t.password}</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.enterPassword}
                required
              />
              <button 
                type="button" 
                className="toggle-password-button"
                onClick={togglePassword}
                aria-label={showPassword ? t.hidePassword : t.showPassword}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="modal-error">
              {error}
            </div>
          )}

          <div className="form-links">
            <Link to="/forgot-password" className="forgot-password-link" onClick={onClose}>
              {t.forgotPassword}
            </Link>
          </div>

          <button type="submit" className="modal-submit-button" disabled={loading}>
            {loading ? (t.signingIn || 'Signing in...') : t.login}
          </button>
        </form>

        <div className="modal-alt-action">
          <p>
            {t.noAccount}{' '}
            <Link to="/register" className="modal-link" onClick={onClose}>
              {t.signUp}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
